/**
 * Lignes de comparaison des tuiles de la section Ventes.
 *
 * Deux familles, parce qu'un écart ne se lit pas pareil selon ce qu'on mesure :
 *  · VOLUMES (CA, commandes, abonnés) — écart RELATIF, « +12 % » ;
 *  · TAUX (churn, pénétration) — écart en POINTS, « +1,2 pt ». Annoncer
 *    « +5,7 % » sur un taux passé de 21 à 22,2 % se ferait lire comme 5,7
 *    points par la moitié des lecteurs.
 *
 * Dans les deux cas, la valeur de référence suit en retrait : « +12 % » seul
 * ne dit pas si l'on parle de 50 € ou de 50 000 €.
 */

import type { StatDelta } from '../components/StatTile';
import { formatDecimal } from './format';
import { relativeChange } from './sales';
import { pointsVerdict, relativeVerdict } from './verdict';

export interface ReferenceSpec {
  /** « période préc. », « l'an dernier »… */
  label: string;
  /**
   * La fenêtre de référence a-t-elle de quoi comparer ? Faux quand elle est
   * vide de toute activité (boutique pas encore ouverte, pas d'abonné) : la
   * ligne dit alors « aucune donnée », ni hausse ni « stable ».
   */
  available: boolean;
}

const signed = (value: number) => `${value >= 0 ? '+' : '−'}${formatDecimal(Math.abs(value))}`;

/** Écart relatif d'un volume. */
export function volumeDelta(
  spec: ReferenceSpec,
  current: number | null | undefined,
  reference: number | null | undefined,
  format: (value: number) => string,
  higherIsBetter: boolean | null,
  emptyText: string,
): StatDelta {
  if (!spec.available) return { text: `${emptyText} ${spec.label}`, verdict: 'insufficient', higherIsBetter };
  if (current == null || reference == null) {
    return { text: `vs ${spec.label}`, verdict: 'insufficient', higherIsBetter };
  }
  const change = relativeChange(current, reference);
  return {
    text: change === null ? `vs ${spec.label}` : `${signed(change)} % vs ${spec.label}`,
    // Référence à zéro (aucun remboursement l'an dernier) : pas de relatif,
    // mais la valeur de référence reste dite.
    verdict: change === null ? (current === reference ? 'flat' : 'insufficient') : relativeVerdict(change),
    higherIsBetter,
    note: format(reference),
  };
}

/** Écart en points d'un taux, en %. */
export function rateDelta(
  spec: ReferenceSpec,
  current: number | null | undefined,
  reference: number | null | undefined,
  higherIsBetter: boolean | null,
  emptyText: string,
): StatDelta {
  if (!spec.available || reference == null) {
    return { text: `${emptyText} ${spec.label}`, verdict: 'insufficient', higherIsBetter };
  }
  if (current == null) return { text: `vs ${spec.label}`, verdict: 'insufficient', higherIsBetter };
  const points = Math.round((current - reference) * 10) / 10;
  return {
    text: `${signed(points)} pt vs ${spec.label}`,
    verdict: pointsVerdict(points),
    higherIsBetter,
    note: `${formatDecimal(reference)} %`,
  };
}
